/**
 * /dev-login — inloggning med custom token, bara i dev.
 *
 * Varför den finns: automatiserad testning av alla tre rollerna utan att
 * någon skriver ett lösenord. Admin SDK präglar en token för ett testkonto
 * (scripts/dev/mint-token.js), den här sidan lämnar över den till appens
 * Auth-instans med signInWithCustomToken, och routar sedan på roll precis som
 * den vanliga inloggningen gör.
 *
 * Varför den aldrig når prod: DEV_LOGIN_ENABLED beräknas ur två byggtids-
 * variabler som CRA inlinar. I ett produktionsbygge blir uttrycket
 * `false && …`, routen registreras inte (App.jsx), och den här komponenten
 * vägrar dessutom köra om den mot förmodan skulle renderas.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';

export const DEV_LOGIN_ENABLED =
  process.env.REACT_APP_ENV !== 'production' &&
  process.env.REACT_APP_FIREBASE_PROJECT_ID === 'hyria-dev';

type Status = { kind: 'idle' | 'working' | 'ok' | 'error'; text: string };

function routeForRole(role: string | undefined): string {
  if (role === 'brfadmin') return '/dashboard';
  if (role === 'hyria_admin') return '/hyria-dashboard';
  return '/';
}

export default function DevLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get('token') ?? '');
  const [status, setStatus] = useState<Status>({ kind: 'idle', text: '' });

  const signIn = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setStatus({ kind: 'error', text: 'Ingen token.' });
      return;
    }
    setStatus({ kind: 'working', text: 'Loggar in…' });
    try {
      // Tokenen ska inte ligga kvar i adressfältet eller historiken.
      window.history.replaceState(null, '', '/dev-login');
      const cred = await signInWithCustomToken(auth, trimmed);
      const snap = await getDoc(doc(db, 'users', cred.user.uid));
      const role = snap.exists() ? (snap.data().role as string | undefined) : undefined;
      setStatus({ kind: 'ok', text: `Inloggad som ${cred.user.email ?? cred.user.uid} (${role ?? 'ingen roll'}).` });
      navigate(routeForRole(role));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setStatus({ kind: 'error', text: `Inloggningen misslyckades: ${message}` });
    }
  };

  useEffect(() => {
    if (DEV_LOGIN_ENABLED && token) {
      void signIn(token);
    }
    // Bara vid första renderingen: tokenen kommer ur URL:en.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!DEV_LOGIN_ENABLED) {
    return (
      <main style={{ maxWidth: 480, margin: '80px auto', fontFamily: 'system-ui, sans-serif' }}>
        <h1 style={{ fontSize: 20 }}>Inte tillgänglig i den här miljön</h1>
        <p>Dev-inloggning finns bara i utvecklingsmiljön mot hyria-dev.</p>
      </main>
    );
  }

  return (
    <main style={{ maxWidth: 480, margin: '80px auto', fontFamily: 'system-ui, sans-serif' }}>
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>Dev-inloggning</h1>
      <p style={{ color: '#555', marginTop: 0 }}>
        Klistra in en custom token från <code>scripts/dev/mint-token.js</code>. Bara hyria-dev.
      </p>
      <label htmlFor="dev-login-token" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        Token
      </label>
      <textarea
        id="dev-login-token"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        rows={5}
        style={{ width: '100%', fontFamily: 'ui-monospace, monospace', fontSize: 12, padding: 8, boxSizing: 'border-box' }}
      />
      <button
        type="button"
        onClick={() => void signIn(token)}
        disabled={status.kind === 'working'}
        style={{ marginTop: 10, padding: '8px 14px', fontSize: 14 }}
      >
        Logga in
      </button>
      {status.text && (
        <p
          role={status.kind === 'error' ? 'alert' : 'status'}
          style={{ marginTop: 14, color: status.kind === 'error' ? '#b42318' : '#1f6a99' }}
        >
          {status.text}
        </p>
      )}
    </main>
  );
}
